import { supabase } from '@/shared/lib/supabase';
import type { PdvCardapioItem, PdvConfigEmpresa, PdvSnapshotEngajamento, PdvSyncStatus } from '@/shared/types';
import type { IPdvEngajamentoRepository } from '../domain/ports/IPdvEngajamentoRepository';

const SNAPSHOT_VAZIO: PdvSnapshotEngajamento = {
  ativo: false,
  contadores: { hype_hoje: 0, hype_ultima_hora: 0, proximo_marco: null, hype_rotulo: 'chopps' },
};

export const pdvEngajamentoRepository: IPdvEngajamentoRepository = {
  async snapshot(telaId) {
    const { data, error } = await supabase.rpc('pdv_snapshot_engajamento' as never, { p_tela_id: telaId } as never);
    if (error) throw error;
    return { ...SNAPSHOT_VAZIO, ...(data as Partial<PdvSnapshotEngajamento> | null) };
  },
  async status(empresaId) {
    const { data, error } = await supabase.rpc('pdv_sync_status' as never, { p_empresa_id: empresaId } as never);
    if (error) throw error;
    return (data as PdvSyncStatus[] | null)?.[0] ?? null;
  },
  async config(empresaId) {
    const { data, error } = await supabase.from('pdv_config_empresa').select('empresa_id,provedor,id_partner,cod_store,mesas,ativo,poll_intervalo_seg,marcos_hype,hype_rotulo,janela_destaque_seg,ultimo_sync_em,ultimo_erro,ultimo_erro_em').eq('empresa_id', empresaId).maybeSingle();
    if (error) throw error;
    return data as PdvConfigEmpresa | null;
  },
  async listarCardapio(empresaId) {
    // Inclui desativados no PDV: o admin marca "Hype" antes de ativar o produto e os
    // consumos já gravados com esse item entram no contador assim que o papel mudar.
    const { data, error } = await supabase.from('pdv_cardapio_itens').select('*').eq('empresa_id', empresaId)
      .order('habilitado', { ascending: false }).order('categoria').order('nome');
    if (error) throw error;
    return (data ?? []) as PdvCardapioItem[];
  },
  async marcarItem(empresaId, itemId, marcacao) {
    const videoFinal = marcacao.video_url?.trim() || null;
    // Imagem e vídeo são mutuamente exclusivos. Como PdvCardapioItemMarcacao não
    // expõe timestamp de alteração, o vídeo vence deterministicamente se ambos vierem
    // preenchidos (prioridade visual na TV), garantindo que nunca zere ambos.
    const imagemFinal = videoFinal ? null : marcacao.imagem_url?.trim() || null;

    const { error } = await supabase.from('pdv_cardapio_itens').update({
      papel: marcacao.papel,
      nome_exibicao: marcacao.nome_exibicao?.trim() || null,
      frase: marcacao.frase?.trim() || null,
      imagem_url: imagemFinal,
      video_url: videoFinal,
    } as never).eq('empresa_id', empresaId).eq('id', itemId);
    if (error) throw error;
  },
  async salvarRegras(empresaId, regras) {
    const { error } = await supabase.rpc('pdv_config_atualizar_regras' as never, {
      p_empresa_id: empresaId, p_mesas: regras.mesas, p_marcos_hype: regras.marcos_hype,
      p_hype_rotulo: regras.hype_rotulo, p_janela_destaque_seg: regras.janela_destaque_seg,
    } as never);
    if (error) throw error;
  },
  async sincronizarCardapio(empresaId: string) {
    const { error } = await supabase.rpc('pdv_solicitar_sync_cardapio' as never, {
      p_empresa_id: empresaId,
    } as never);
    if (error) throw error;
  },
};
